import React, { useState } from 'react';
import { CreatePage } from '../../../components/crud/CreatePage';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../components/ui/use-toast';
import { useCreate, useSelect } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';

export const AnalyzerDeviceCreate: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { mutate, isLoading } = useCreate();
  const { options: connectorOptions } = useSelect({ resource: 'devices/connectors', optionLabel: 'name', optionValue: 'id' });

  const [values, setValues] = useState({
    name: '', category: '', manufacturer: '', model: '', serial_number: '',
    connector_device: '', connection_type: 'tcp', device_ip: '', device_port: '', protocol: 'hl7',
    serial_port: 'COM1', baud_rate: '9600', parity: 'N', data_bits: '8', stop_bits: '1',
    lis_ip: '', lis_port: '', qc_enabled: true, qc_frequency_hours: '24', status: 'active',
    calibration_due_date: '', maintenance_due_date: '', description: '',
    supported_tests: '',
    hl7_version: '2.3.1', ack_mode: 'original', mllp_enabled: true, message_types: 'ORU^R01',
  });
  const set = (f: string, v: any) => setValues(p => ({ ...p, [f]: v }));

  const handleSave = () => {
    if (!values.name || !values.category || !values.manufacturer || !values.model || !values.serial_number) {
      toast({ variant: 'destructive', title: 'Name, category, manufacturer, model and serial number are required' }); return;
    }
    if (!values.connector_device) {
      toast({ variant: 'destructive', title: 'Connector device is required' }); return;
    }

    const connection_config: Record<string, any> = {};
    if (values.description) connection_config.description = values.description;
    if (values.connection_type === 'serial') {
      Object.assign(connection_config, {
        serial_port: values.serial_port,
        baud_rate: Number(values.baud_rate),
        parity: values.parity,
        data_bits: Number(values.data_bits),
        stop_bits: Number(values.stop_bits),
      });
    }

    const protocol_config: Record<string, any> = {};
    if (values.protocol === 'hl7') {
      Object.assign(protocol_config, {
        hl7_version: values.hl7_version,
        ack_mode: values.ack_mode,
        mllp_enabled: values.mllp_enabled,
        message_types: values.message_types.split(',').map(s => s.trim()).filter(Boolean),
      });
    }

    const supported_tests = values.supported_tests
      ? values.supported_tests.split(',').map(s => s.trim()).filter(Boolean)
      : [];

    mutate({ resource: 'devices/analyzers', values: {
      name: values.name, category: values.category, manufacturer: values.manufacturer,
      model: values.model, serial_number: values.serial_number,
      connector_device: values.connector_device, connection_type: values.connection_type,
      protocol: values.protocol, status: values.status,
      device_ip: values.device_ip || undefined,
      device_port: values.device_port ? Number(values.device_port) : undefined,
      lis_ip: values.lis_ip || undefined,
      lis_port: values.lis_port ? Number(values.lis_port) : undefined,
      qc_enabled: values.qc_enabled,
      qc_frequency_hours: Number(values.qc_frequency_hours),
      calibration_due_date: values.calibration_due_date || undefined,
      maintenance_due_date: values.maintenance_due_date || undefined,
      connection_config,
      protocol_config,
      supported_tests,
    }}, {
      onSuccess: () => { toast({ title: 'Analyzer device created' }); navigate('/devices/analyzers'); },
      onError: (e: any) => toast({ variant: 'destructive', title: 'Failed to create analyzer', description: e?.message }),
    });
  };

  return (
    <CreatePage title="Create Analyzer Device" footerButtons={<Button onClick={handleSave} disabled={isLoading}>{isLoading ? 'Saving...' : 'Save'}</Button>}>
      <div className="space-y-6">
        {/* Basic Info */}
        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">Basic Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Analyzer Name <span className="text-destructive">*</span></Label><Input placeholder="Enter analyzer name" value={values.name} onChange={e => set('name', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Category <span className="text-destructive">*</span></Label>
              <Select value={values.category} onValueChange={v => set('category', v)}><SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {['chemistry', 'hematology', 'immunology', 'microbiology', 'molecular', 'urinalysis', 'coagulation', 'blood_gas', 'other'].map(c =>
                    <SelectItem key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>)}
                </SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Manufacturer <span className="text-destructive">*</span></Label><Input value={values.manufacturer} onChange={e => set('manufacturer', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Model <span className="text-destructive">*</span></Label><Input value={values.model} onChange={e => set('model', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Serial Number <span className="text-destructive">*</span></Label><Input value={values.serial_number} onChange={e => set('serial_number', e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} placeholder="Technology, sample type, throughput…" value={values.description} onChange={e => set('description', e.target.value)} /></div>
        </CardContent></Card>

        {/* Connection */}
        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">Connection Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Connector Device <span className="text-destructive">*</span></Label>
              <Select value={values.connector_device} onValueChange={v => set('connector_device', v)}><SelectTrigger><SelectValue placeholder="Select connector" /></SelectTrigger>
                <SelectContent>{(connectorOptions || []).map((o: any) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Connection Type <span className="text-destructive">*</span></Label>
              <Select value={values.connection_type} onValueChange={v => set('connection_type', v)}><SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{['serial', 'tcp', 'usb', 'ethernet', 'bluetooth', 'wifi'].map(t => <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Device IP</Label><Input placeholder="192.168.0.175" value={values.device_ip} onChange={e => set('device_ip', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Device Port</Label><Input type="number" min={1} max={65535} placeholder="6005" value={values.device_port} onChange={e => set('device_port', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Protocol <span className="text-destructive">*</span></Label>
              <Select value={values.protocol} onValueChange={v => set('protocol', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="hl7">HL7 MLLP</SelectItem><SelectItem value="astm">ASTM E1394</SelectItem><SelectItem value="lis2a">LIS2-A</SelectItem><SelectItem value="clsi">CLSI LIS</SelectItem><SelectItem value="serial">Serial ASCII</SelectItem><SelectItem value="tcp">TCP/IP Raw</SelectItem><SelectItem value="custom">Custom</SelectItem></SelectContent></Select></div>
          </div>
        </CardContent></Card>

        {/* Serial Port Config (only relevant for serial) */}
        {values.connection_type === 'serial' && (
        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">Serial Port Configuration</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="space-y-1.5"><Label>Port</Label><Input value={values.serial_port} onChange={e => set('serial_port', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Baud Rate</Label>
              <Select value={values.baud_rate} onValueChange={v => set('baud_rate', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['9600', '19200', '38400', '57600', '115200'].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Parity</Label>
              <Select value={values.parity} onValueChange={v => set('parity', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="N">None</SelectItem><SelectItem value="E">Even</SelectItem><SelectItem value="O">Odd</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Data Bits</Label>
              <Select value={values.data_bits} onValueChange={v => set('data_bits', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="7">7</SelectItem><SelectItem value="8">8</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Stop Bits</Label>
              <Select value={values.stop_bits} onValueChange={v => set('stop_bits', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="1">1</SelectItem><SelectItem value="2">2</SelectItem></SelectContent></Select></div>
          </div>
        </CardContent></Card>
        )}

        {/* LIS */}
        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">LIS Server Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>LIS IP Address</Label><Input placeholder="192.168.0.171" value={values.lis_ip} onChange={e => set('lis_ip', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>LIS Port</Label><Input type="number" min={1} max={65535} placeholder="5005" value={values.lis_port} onChange={e => set('lis_port', e.target.value)} /></div>
          </div>
        </CardContent></Card>

        {/* Protocol config (HL7) */}
        {values.protocol === 'hl7' && (
        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">HL7 Protocol Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>HL7 Version</Label>
              <Select value={values.hl7_version} onValueChange={v => set('hl7_version', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['2.2', '2.3', '2.3.1', '2.4', '2.5', '2.5.1'].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>ACK Mode</Label>
              <Select value={values.ack_mode} onValueChange={v => set('ack_mode', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="original">Original</SelectItem><SelectItem value="enhanced">Enhanced</SelectItem></SelectContent></Select></div>
            <div className="flex items-center gap-2 pt-6"><Switch checked={values.mllp_enabled} onCheckedChange={v => set('mllp_enabled', v)} /><Label>MLLP Enabled</Label></div>
          </div>
          <div className="space-y-1.5"><Label>Message Types (comma separated)</Label><Input placeholder="ORU^R01, ORM^O01" value={values.message_types} onChange={e => set('message_types', e.target.value)} /></div>
        </CardContent></Card>
        )}

        {/* Tests */}
        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">Supported Tests</h4>
          <div className="space-y-1.5"><Label>Test Codes (comma separated)</Label><Textarea rows={2} placeholder="WBC, RBC, HGB, HCT, PLT…" value={values.supported_tests} onChange={e => set('supported_tests', e.target.value)} /></div>
        </CardContent></Card>

        {/* QC & Maintenance */}
        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">Quality Control & Maintenance</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2"><Switch checked={values.qc_enabled} onCheckedChange={v => set('qc_enabled', v)} /><Label>Enable QC</Label></div>
            <div className="space-y-1.5"><Label>QC Frequency (Hours)</Label><Input type="number" min={1} max={168} value={values.qc_frequency_hours} onChange={e => set('qc_frequency_hours', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={values.status} onValueChange={v => set('status', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem></SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Calibration Due Date</Label><Input type="date" value={values.calibration_due_date} onChange={e => set('calibration_due_date', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Maintenance Due Date</Label><Input type="date" value={values.maintenance_due_date} onChange={e => set('maintenance_due_date', e.target.value)} /></div>
          </div>
        </CardContent></Card>
      </div>
    </CreatePage>
  );
};

export default AnalyzerDeviceCreate;
